`use strict`;

class Draad_Sliders {

	constructor( slider, sliderIndex ) {
		this.slider = slider;
		this.sliderIndex = sliderIndex;

		// get computedStyle of this.slider for the settings of this slider
		this.sectionComputed = getComputedStyle( this.slider );

		//slider conditional option
		this.sliderActive = this.sectionComputed.getPropertyValue('--slider-active') || 'show';

		// find slider controls
		this.sliderControls = this.slider.querySelector('.slider__controls');

		if ( this.sliderActive !== 'show' ) {
			if ( this.sliderControls ) {
				this.sliderControls.setAttribute('hidden', 'hidden');
			}
			return;
		}
		
		// slider methods
		this.generalSliderProperties();
		this.generalQuerySelectors();
		this.prepareSlider();
		this.initializeSlider();

		//resize event
		window.addEventListener('resize', this.onResizeChanges.bind( this ) );
	}

	initializeSlider() {    
		this.swiper = new Swiper( this.slider, {
			autoHeight: this.sliderAutoHeight(),
			autoplay: this.sliderAutoPlay(),
			effect: this.sliderEffect(),
			cardsEffect: this.sliderEffectCards(),
			coverflowEffect: this.sliderEffectCoverflow(),
			fadeEffect: this.sliderEffectFade(),
			grid: this.sliderGrid(),
			hashNavigation: this.sliderHashNavigation(),
			keyboard: true,
			loop: this.sliderLoop(),
			navigation: this.sliderNavigation(),
			on: this.sliderOnChange(),
			pagination: this.sliderPagination(),
			spaceBetween: this.sliderSpaceBetween(),
			slidesPerView: this.sliderSlides(),
			slidesPerGroup: this.sliderSlidesPerGroup(),
			speed: this.sliderSpeed(),
		});
	}

	generalSliderProperties() {
		this.autoHeight = this.sectionComputed.getPropertyValue('--autoheight') || false;
		this.autoPlay = this.sectionComputed.getPropertyValue('--autoplay') || false;
		this.effect = this.sectionComputed.getPropertyValue('--effect') || 'slide';
		this.grid = this.sectionComputed.getPropertyValue('--grid') || false;
		this.hashNavigation = this.sectionComputed.getPropertyValue('--hash-navigation') || false;
		this.loop = this.sectionComputed.getPropertyValue('--loop') || false;
		this.pagination = this.sectionComputed.getPropertyValue('--pagination') || 'show';
		this.paginationType = this.sectionComputed.getPropertyValue('--pagination-type') || 'progressbar';
		this.navigation = this.sectionComputed.getPropertyValue('--navigation') || 'show';
		this.numbers = this.sectionComputed.getPropertyValue('--numbers') || 'show';
	}

	generalQuerySelectors() {
		if ( this.sliderControls ) {
			this.pagerSelector = this.slider.querySelector('.slider__pager');
			this.nextSelector = this.slider.querySelector('.slider__next');
			this.prevSelector = this.slider.querySelector('.slider__prev');
			this.currentSelector = this.slider.querySelector('.slider__current');
			this.totalSelector = this.slider.querySelector('.slider__total');
		}
		this.slidesSelector = this.slider.querySelector('.slider__slides');
		this.itemsSelector = Array.from( this.slidesSelector.children );
	}

	prepareSlider() {
		this.slider.classList.add('swiper');
		this.slidesSelector.classList.add('swiper-wrapper');

		const hashNavigation = this.hashNavigation;
		const sliderIndex = this.sliderIndex;
		this.itemsSelector.forEach( function ( slide, index ) {
			slide.classList.add('swiper-slide');
			slide.classList.add('slider__slide');

			if ( hashNavigation === 'show' ) {
				slide.setAttribute('data-hash', `slide${index + '-slider' + sliderIndex}`);
			}

			index++;
		});

		if ( this.sliderControls ) {
			( this.pagination === 'show' ) ? this.pagerSelector.classList.add('swiper-pagination') : this.pagerSelector.setAttribute('hidden', 'hidden');

			if ( this.navigation !== 'show' ) { 
				this.nextSelector.setAttribute('hidden', 'hidden');
				this.prevSelector.setAttribute('hidden', 'hidden');
			}
	
			if ( this.numbers !== 'show' ) {
				this.currentSelector.setAttribute('hidden', 'hidden');
				this.totalSelector.setAttribute('hidden', 'hidden');
			}
		}
	}

	sliderAutoHeight() {
		if ( this.autoHeight !== 'show' ) {
			return false;
		}
		
		return true;
	}

	sliderAutoPlay( autoPlay = {} ) {
		if ( this.autoPlay !== 'show' ) {
			return false;
		}

		this.autoPlayDelay = this.sectionComputed.getPropertyValue('--autoplay-delay') || 3000;

		autoPlay = {
			delay: this.autoPlayDelay,
		};

		return autoPlay;
	}

	// all slider effects
	sliderEffect() {
		return this.effect;
	}

	sliderEffectCards( cardsEffect = {} ) {
		if ( this.effect !== 'cards' ) {
			return false;
		}

		this.cardsEffectPerSlideOffset = this.sectionComputed.getPropertyValue('--cards-per-slide-offset') || 20;

		this.cardsEffectPerSlideRotate = this.sectionComputed.getPropertyValue('--cards-per-slide-rotate') || 2;

		this.cardsEffectRotate = this.sectionComputed.getPropertyValue('--cards-rotate') || 'show';
		this.cardsEffectRotate = ( this.cardsEffectRotate === 'show' ) ? true : false;

		this.cardsEffectSlideShadows = this.sectionComputed.getPropertyValue('--cards-slide-shadows') || 'show';
		this.cardsEffectSlideShadows = ( this.cardsEffectSlideShadows === 'show' ) ? true : false;

		cardsEffect = {
			perSlideOffset: this.cardsEffectPerSlideOffset,
			perSlideRotate: this.cardsEffectPerSlideRotate,
			rotate: this.cardsEffectRotate,
			slideShadows: this.cardsEffectSlideShadows,
		};

		return cardsEffect;
	}

	sliderEffectCoverflow( coverflowEffect = {} ) {
		if ( this.effect !== 'coverflow' ) {
			return false;
		}

		this.coverflowEffectDepth = this.sectionComputed.getPropertyValue('--coverflow-depth') || 100;
		this.coverflowEffectModifier = this.sectionComputed.getPropertyValue('--coverflow-modifier') || 1;
		this.coverflowEffectRotate = this.sectionComputed.getPropertyValue('--coverflow-rotate') || 50;
		this.coverflowEffectScale = this.sectionComputed.getPropertyValue('--coverflow-scale') || 1;
		this.coverflowEffectSlideShadows = this.sectionComputed.getPropertyValue('--coverflow-slide-shadows') || 'show';
		this.coverflowEffectSlideShadows = ( this.coverflowEffectSlideShadows === 'show' ) ? true : false;
		this.coverflowEffectStretch = this.sectionComputed.getPropertyValue('--coverflow-stretch') || 0;

		coverflowEffect = {
			depth: this.coverflowEffectDepth,
			modifier: this.coverflowEffectModifier,
			rotate: this.coverflowEffectRotate,
			scale: this.coverflowEffectScale,
			slideShadows: this.coverflowEffectSlideShadows,
			stretch: this.coverflowEffectStretch,
		};

		return coverflowEffect;
	}

	sliderEffectFade( fadeEffect = {} ) {
		if ( this.effect !== 'fade' ) {
			return false;
		}

		this.fadeEffectCrossFade = this.sectionComputed.getPropertyValue('--fade-crossfade') || 'show';
		this.fadeEffectCrossFade = ( this.fadeEffectCrossFade === 'show' ) ? true : false;

		fadeEffect = {
			crossFade: this.fadeEffectCrossFade,
		};

		return fadeEffect;
	}
	// end of slider effects

	sliderGrid( rows = {} ) {
		if ( this.grid !== 'show' ) {
			return false;
		}

		this.gridRows = this.sectionComputed.getPropertyValue('--grid-rows') || 1;

		rows = {
			fill: 'row',
			rows: this.gridRows,
		};

		return rows;
	}

	sliderHashNavigation( ) {
		if ( this.hashNavigation !== 'show' ) {
			return false;
		}

		return true;
	}

	sliderLoop() {
		if ( this.loop !== 'show' ) {
			return false;
		}

		return true;
	}

	sliderNavigation( navigation = {} ) {
		if ( this.navigation !== 'show' ) {
			return false;
		}

		navigation = {
			nextEl: this.nextSelector,
			prevEl: this.prevSelector,
		};

		return navigation;
	}

	sliderOnChange( on = {} ) {
		if ( this.numbers !== 'show' || !this.sliderControls ) {
			return false;
		}

		const currentSelector = this.currentSelector;

		on = {
			slideChange: function () {
				currentSelector.innerHTML = ( this.realIndex < 9 ) ? '0' + ( this.realIndex + 1 ) : this.realIndex + 1;
			},
		};

		return on;
	}

	sliderPagination( pagination = {} ) {
		if ( this.pagination !== 'show' || !this.sliderControls ) {
			return false;
		}

		pagination = {
			el: '.slider__pager',
			type: this.paginationType,
		};

		if ( this.paginationType === 'bullets' ) {
			pagination.clickable = true;

			let bullets = this.slider.querySelector('.slider__bullets');

			if ( bullets ) {
				bullets = bullets.children;

				pagination.renderBullet = function ( index, className ) {
					return '<span class="' + className + '">' + bullets[index].innerHTML + '</span>';
				}
			}
		}

		this.dynamicBullets = this.sectionComputed.getPropertyValue('--pagination-dynamic-bullets') || false;
		this.dynamicBulletsVisible = this.sectionComputed.getPropertyValue('--pagination-dynamic-bullets-visible') || 3;
		this.dynamicBulletsVisible = parseInt( this.dynamicBulletsVisible );

		if ( this.paginationType !== 'bullets' || this.dynamicBullets !== 'show' || this.dynamicBulletsVisible <= 0 ) {
			return pagination;
		}

		pagination.dynamicBullets = true;
		pagination.dynamicMainBullets = this.dynamicBulletsVisible;

		return pagination;
	}

	sliderSpaceBetween() {
		this.spaceBetween = this.sectionComputed.getPropertyValue('--space-between') || 0;

		return this.spaceBetween;
	}

	sliderSpeed() {
		this.speed = this.sectionComputed.getPropertyValue('--speed') || 300;

		return this.speed;
	}

	sliderSlides() {
		this.slides = this.sectionComputed.getPropertyValue('--slides') || 1;

		return this.slides;
	}

	sliderSlidesPerGroup() {
		this.slidesPerGroup = this.sectionComputed.getPropertyValue('--slides-per-group') || 1;

		return this.slidesPerGroup;
	}

	onResizeChanges() {
		this.sliderAutoHeight();
		this.sliderAutoPlay();
		this.sliderEffect();
		this.sliderEffectCards();
		this.sliderEffectCoverflow();
		this.sliderEffectFade();
		this.sliderGrid();
		this.sliderHashNavigation();
		this.sliderLoop();
		this.sliderNavigation();
		this.sliderOnChange();
		this.sliderPagination();
		this.sliderSpaceBetween();
		this.sliderSlides();
		this.sliderSlidesPerGroup();
		this.sliderSpeed();
	}
}

document.addEventListener("DOMContentLoaded", () => {
	
	const draadSliders = [];

	const draadSlidersNodes = document.querySelectorAll('.slider');
	let sliderIndex = 0;

	draadSlidersNodes.forEach( function( slider ) {

		draadSliders.push( new Draad_Sliders( slider, sliderIndex++ ) );

	});

});